import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

type ToolCardProps = {
  title: string;
  description: string;
  actionLabel: string;
  onClick: () => void;
};

const ToolCard = ({
  title,
  description,
  actionLabel,
  onClick,
}: ToolCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex w-full flex-col rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
  >
    <div className="inline-flex w-fit rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
      Tools
    </div>
    <h2 className="mt-4 text-2xl font-bold text-slate-950">{title}</h2>
    <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
      {description}
    </p>
    <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">
      {actionLabel}
      <span
        aria-hidden="true"
        className="transition group-hover:translate-x-0.5"
      >
        →
      </span>
    </div>
  </button>
);

export default function OtherPage() {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const userTier = String(user?.tier || "").toLowerCase();
  const canSeeAppTools = ["pro", "elite"].includes(userTier);

  return (
    <main className="min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-10 md:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="space-y-2">
          <h1 className="text-3xl font-bold text-slate-950">Tools</h1>
          <p className="max-w-2xl text-sm text-slate-600">
            Free planning tools for event vendors.
          </p>
        </header>

        <div className="grid gap-4 lg:grid-cols-2">
          <ToolCard
            title="Booth Setup Planner"
            description="Plan your show floor with a scaled grid, booth fixtures, starter layouts, and print-ready floor plans."
            actionLabel="Open booth planner"
            onClick={() => navigate("/tools/booth-layout")}
          />
          {canSeeAppTools ? (
            <>
              <ToolCard
                title="Inventory Import & Export"
                description="Download the latest Excel template, add inventory in bulk, or update existing items by SKU."
                actionLabel="Import or update inventory"
                onClick={() => navigate("/import")}
              />
              <ToolCard
                title="TikTok Live Auctions"
                description="Capture wins, reconcile buyers, package orders, and review auction reports."
                actionLabel="Open live auctions"
                onClick={() => navigate("/live-auctions")}
              />
              <ToolCard
                title="PDF Editor"
                description="Fill labels and forms, place saved customer fields, and export finished PDFs."
                actionLabel="Open PDF editor"
                onClick={() => navigate("/pdf-editor")}
              />
              <ToolCard
                title="Sales Tax Reports"
                description="Calculate tax liability, compare Square data, review filings, and export report details."
                actionLabel="Open sales tax"
                onClick={() => navigate("/finance/tax-reports")}
              />
            </>
          ) : null}
        </div>
      </div>
    </main>
  );
}
